import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../state/state';
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
} from '@mui/material';
import { PageName } from '../../../../enums.ts';
import { Download, Delete, Assessment } from '@mui/icons-material';
import { openModal } from '../../../../state/ModalSlice/ModalSlice';
import { actions as actions_ExportsDataSlice } from '../../../../state/ExportsDataSlice/ExportsDataSlice.tsx';
import { useBackend } from '../../../../hooks/backend/useBackend.ts';
import { deleteObject, ref } from 'firebase/storage';
import { storage } from '../../../../Config/firebase';
import { DocumentData, Timestamp } from 'firebase/firestore';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { ExportType } from '../../../Common/types/ExportTypes';
import { selectActiveRole, selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2.ts';
import { GenericActions } from '../../../../state/GenericSlice.ts';
import AccessControl from '../../Components/AccessControl';
import { mobileStyles, cn } from '../../constants/mobileStyles';

interface Export {
    RequestedOn: Timestamp;
    id: string;
    Status: string;
    exportType: string;
    StartDate: Timestamp;
    EndDate: Timestamp;
    month: string;
    year: string;
    doc_url: string;
    // ... other fields ...
}

const getDateRangeString = (exportItem: Export): string => {
    if (!exportItem.exportType || exportItem.exportType === 'Payments') {
        return `${exportItem.StartDate ? exportItem.StartDate.toString() : 'N/A'} - ${exportItem.EndDate ? exportItem.EndDate.toString() : 'N/A'}`;
    }
    
    if (exportItem.exportType === 'Attendance') {
        if (exportItem.StartDate && exportItem.EndDate) {
            return `${exportItem.StartDate.toString()} - ${exportItem.EndDate.toString()}`;
        }
        if (exportItem.month && exportItem.year) {
            return `${exportItem.month}-${exportItem.year}`;
        }
        return 'N/A';
    }
    
    return 'N/A';
};

const ExportHistory: React.FC = () => {
    // const [exports, setExports] = useState<Export[]>([]);
    const [loading, setLoading] = useState(true);
    const providerId = useAppSelector(selectEffectiveUID);
    const dispatch = useAppDispatch()
    const activeRole = useAppSelector(selectActiveRole);
    const role = activeRole?.role;
    const hasAccess = role ? role === "Administrator" : true;
    const { loadCollection, remove } = useBackend(`ServiceProviders/${providerId}/Exports`)


    const exports = useAppSelector((state) =>
        state.ExportsDataSlice.data as unknown as Export[]
    );

    // You can modify this based on your access control logic

    useEffect(() => {

        if (providerId) {
            loadCollection(actions_ExportsDataSlice as GenericActions<DocumentData>,
                {
                    sort: {
                        attribute: 'RequestedOn',
                        order: 'desc'
                    }
                }
            )
        }
        setLoading(false)

    }, [providerId, loadCollection])



    const getRowStyle = (status: string) => {
        if (status.toLowerCase() === 'success') {
            return { backgroundColor: 'rgba(76, 175, 80, 0.1)' };
        } else if (status.toLowerCase() === 'pending') {
            return { backgroundColor: 'rgba(255, 255, 224, 0.3)' };
        }
        return {};
    };

    const getStatusStyle = (status: string) => {
        if (status.toLowerCase() === 'success') {
            return { color: '#4caf50', fontWeight: 'bold' };
        } else if (status.toLowerCase() === 'pending') {
            return { color: '#FFA500', fontWeight: 'bold' };
        }
        return { fontWeight: 'bold' };
    };

    function handleOpenExportModal(exportType: ExportType) {
        let modalName = 'ExportPaymentsModalOpen';
        switch (exportType) {
            case ExportType.ATTENDANCE:
                modalName = 'ExportAttendanceModalOpen';
                break;
            case ExportType.STUDENT_PROGRESS:
                modalName = 'ExportStudentProgressModalOpen';
                break;
            default:
                modalName = 'ExportPaymentsModalOpen';
        }
        dispatch(openModal(modalName));
    }

    const handleDelete = async (exportItem: Export) => {
        try {
            // Delete the Excel file from Firebase Storage if it exists
            if (exportItem.doc_url) {
                const fileRef = ref(storage, exportItem.doc_url);
                await deleteObject(fileRef);
            }

            // Delete the document from Firestore
            await remove(exportItem.id);

            // Refresh the collection
            loadCollection(actions_ExportsDataSlice, {
                sort: {
                    attribute: 'RequestedOn',
                    order: 'desc'
                }
            });
        } catch (error) {
            console.error('Error deleting export:', error);
            // Optionally show an error message to the user
        }
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="200px"
            >
                <CircularProgress />
            </Box>
        );
    }



    return (
        <AccessControl pageName={PageName.ExportHistory} >
            <div className={mobileStyles.padding.container}><Box>


                {/* Central Export Section */}
                <div className={cn('my-4 sm:my-8 text-center')}>
                    <h1 className={cn(mobileStyles.text.h2, mobileStyles.margin.bottom.medium)}>Export Order Summary</h1>
                    <p className={cn(mobileStyles.text.body, mobileStyles.margin.bottom.medium)}>Generate and download detailed customer order reports in Excel.</p>
                    <div className={cn('flex flex-wrap justify-center gap-2 sm:gap-4 lg:gap-6', mobileStyles.margin.bottom.large)}>
                        <button
                            onClick={() => handleOpenExportModal(ExportType.PAYMENTS)}
                            className={cn(mobileStyles.button.primary, 'bg-cc_brand text-white hover:bg-opacity-90 flex justify-center items-center gap-1.5 sm:gap-2')}>
                            <ReceiptLongIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} /> 
                            <span className="hidden sm:inline">Export Payments</span>
                            <span className="sm:hidden">Payments</span>
                        </button>
                        <button
                            onClick={() => handleOpenExportModal(ExportType.ATTENDANCE)}
                            className={cn(mobileStyles.button.primary, 'bg-cc_brand text-white hover:bg-opacity-90 flex justify-center items-center gap-1.5 sm:gap-2')}>
                            <PeopleAltIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} /> 
                            <span className="hidden sm:inline">Export Attendance</span>
                            <span className="sm:hidden">Attendance</span>
                        </button>
                        <button
                            onClick={() => handleOpenExportModal(ExportType.STUDENT_PROGRESS)}
                            className={cn(mobileStyles.button.primary, 'bg-cc_brand text-white hover:bg-opacity-90 flex justify-center items-center gap-1.5 sm:gap-2')}>
                            <Assessment sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} /> 
                            <span className="hidden sm:inline">Export Student Progress</span>
                            <span className="sm:hidden">Progress</span>
                        </button>
                        <button
                            className={cn(mobileStyles.button.primary, 'bg-gray-400 text-white cursor-not-allowed flex justify-center items-center gap-1.5 sm:gap-2 relative')}>
                            <ReceiptLongIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} /> 
                            <span className="hidden sm:inline">Archive and Download Invoices</span>
                            <span className="sm:hidden">Invoices</span>
                            <span className="absolute -top-2 -right-2 bg-yellow-500 text-xs text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full whitespace-nowrap">Coming Soon</span>
                        </button>
                    </div>
                </div>

                {/* History Table Section */}
                <div>
                    <h2 className={cn(mobileStyles.text.h4, mobileStyles.margin.bottom.medium)}>Export History</h2>
                    <TableContainer 
                        component={Paper}
                        sx={{
                            maxHeight: { xs: '60vh', sm: '70vh', md: '80vh' },
                            overflowX: 'auto',
                            overflowY: 'auto'
                        }}
                    >
                        <Table sx={{ minWidth: 650 }} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: '600', fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>REQUESTED ON</TableCell>
                                    <TableCell sx={{ fontWeight: '600', fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Export Type</TableCell>
                                    <TableCell sx={{ fontWeight: '600', fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Export Cycle</TableCell>
                                    <TableCell sx={{ fontWeight: '600', fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>STATUS</TableCell>
                                    <TableCell sx={{ fontWeight: '600', fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>EXPORT ID</TableCell>
                                    <TableCell sx={{ fontWeight: '600', fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>ORDER REPORT</TableCell>
                                    <TableCell sx={{ fontWeight: '600', fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exports.filter(exportItem => exportItem.exportType !== 'EventPayments').map((exportItem) => (
                                    <TableRow key={exportItem.id} style={getRowStyle(exportItem.Status)}>
                                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>
                                            {exportItem.RequestedOn ? exportItem.RequestedOn.toString() : 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>
                                            {exportItem.exportType ? exportItem.exportType.toString() : 'Payments'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>
                                            {getDateRangeString(exportItem)}
                                        </TableCell>
                                        <TableCell>
                                            <span style={{
                                                ...getStatusStyle(exportItem.Status),
                                            }}
                                            className="text-xs sm:text-sm font-semibold"
                                            >
                                                {exportItem.Status}
                                            </span>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>{exportItem.id}</TableCell>
                                        <TableCell>
                                            {exportItem.doc_url ? (
                                                <a
                                                    href={exportItem.doc_url}
                                                    download={`order-export-${exportItem.id}.xlsx`}
                                                    className={cn('underline underline-offset-2 flex items-center gap-1 text-blue-600 hover:text-blue-800', mobileStyles.text.bodySmall)}
                                                >
                                                    <Download sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} /> 
                                                    <span className="hidden sm:inline">Download</span>
                                                </a>
                                            ) : exportItem.Status === 'Pending' ? <div className='flex pl-2 sm:pl-6'><CircularProgress size={16} /></div> : 'N/A'
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Delete Export">
                                                <IconButton
                                                    onClick={() => handleDelete(exportItem)}
                                                    color="error"
                                                    size="small"
                                                    sx={{ padding: { xs: '4px', sm: '8px' } }}
                                                >
                                                    <Delete sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>

            </Box></div>
        </AccessControl>
    );
};


export default ExportHistory;
